import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle2, Clock, RotateCcw, XCircle } from "lucide-react";
import { getPublicationStatusLabel, type PublicationStatus } from "@/lib/catalog/catalog-workflow";
import { cn } from "@/lib/utils";

interface CatalogPublicationBannerProps {
  status: PublicationStatus;
  reviewNotes?: string | null;
}

const statusIcons: Record<PublicationStatus, typeof AlertCircle> = {
  DRAFT: Clock,
  PENDING_REVIEW: Clock,
  RETURNED: RotateCcw,
  REJECTED: XCircle,
  PUBLISHED: CheckCircle2,
};

const statusStyles: Record<PublicationStatus, string> = {
  DRAFT: "border-slate-200 bg-slate-50 text-slate-800",
  PENDING_REVIEW: "border-amber-200 bg-amber-50 text-amber-900",
  RETURNED: "border-amber-200 bg-amber-50 text-amber-900",
  REJECTED: "border-red-200 bg-red-50 text-red-900",
  PUBLISHED: "border-green-200 bg-green-50 text-green-900",
};

export function CatalogPublicationBanner({ status, reviewNotes }: CatalogPublicationBannerProps) {
  if (status === "PUBLISHED") return null;

  const Icon = statusIcons[status];
  const isReadOnly = status === "REJECTED" || status === "PENDING_REVIEW";

  return (
    <Alert className={cn("mb-6", statusStyles[status])}>
      <Icon className="h-4 w-4" />
      <p className="font-semibold">{getPublicationStatusLabel(status)}</p>
      <AlertDescription className="space-y-1">
        {status === "DRAFT" && (
          <p>This record is a draft and is not visible in the public catalog.</p>
        )}
        {status === "PENDING_REVIEW" && (
          <p>This record is awaiting reviewer approval and cannot be edited.</p>
        )}
        {status === "RETURNED" && (
          <p>Review feedback was provided. Update the record and submit again for review.</p>
        )}
        {status === "REJECTED" && (
          <p>This record was rejected and is view-only. Create a new entry if needed.</p>
        )}
        {reviewNotes ? <p className="font-medium">Reviewer notes: {reviewNotes}</p> : null}
        {isReadOnly ? <p>Editing is disabled for this publication status.</p> : null}
      </AlertDescription>
    </Alert>
  );
}
